// Global auto-suggest popover for the custom dictionary.
// Watches every input / textarea / contenteditable across the admin app and
// pops a suggestion when the current word looks like a mistyped variant of a
// dictionary word (Levenshtein distance <= 2) OR matches a known alias.
//
// Accept:  Tab / Enter / click on suggestion
// Dismiss: Esc / blur / click outside
// Navigate: ArrowUp / ArrowDown when multiple suggestions
import React, { useEffect, useRef, useState } from 'react';

// Common pet-travel typos that fuzzy match may miss (map lowercase typo -> [Correct])
const ALIAS_MAP = {
  kavapoo: ['Cavapoo'],
  cavapoodle: ['Cavapoo'],
  cavachone: ['Cavachon'],
  cockerpoo: ['Cockapoo'],
  daschund: ['Dachshund'],
  dachsund: ['Dachshund'],
  rottweiller: ['Rottweiler'],
  rotweiler: ['Rottweiler'],
  weimeraner: ['Weimaraner'],
  vizla: ['Vizsla'],
  chiwawa: ['Chihuahua'],
  pommeranian: ['Pomeranian'],
  fahvn: ['FAVN'],
  favin: ['FAVN'],
  favnn: ['FAVN'],
  micochip: ['microchip'],
  microship: ['microchip'],
  rabbies: ['rabies'],
  rabies: ['rabies'],
  endorsment: ['endorsement'],
  endoresement: ['endorsement'],
  crat: ['crate'],
  kayman: ['Cayman'],
  caymen: ['Cayman'],
  defrra: ['DEFRA'],
  apphis: ['APHIS'],
  usdaa: ['USDA'],
  iatta: ['IATA'],
  pawsom: ['Pawsome'],
};

// Bounded Levenshtein — early-exits when the diagonal minimum passes `max`.
function levenshtein(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[n];
}

function findSuggestions(word, dict) {
  if (!word || word.length < 3) return [];
  const lower = word.toLowerCase();

  // If the current word is already a dictionary term, don't suggest.
  for (const w of dict) if (w.toLowerCase() === lower) return [];

  // Alias hits win.
  if (ALIAS_MAP[lower]) return ALIAS_MAP[lower];

  // Fuzzy match against dictionary words of similar length.
  const matches = [];
  for (const w of dict) {
    const wl = w.toLowerCase();
    if (wl === lower) continue;
    const d = levenshtein(lower, wl, 2);
    if (d > 0 && d <= 2) matches.push({ w, d });
  }
  matches.sort((a, b) => a.d - b.d || a.w.length - b.w.length);
  return matches.slice(0, 3).map((m) => m.w);
}

// Get pixel coordinates of the caret inside a plain <input> / <textarea>.
// Uses a hidden mirror <div> that copies the element's style so we can measure.
function getInputCaretRect(element, position) {
  const isInput = element.tagName === 'INPUT';
  const div = document.createElement('div');
  const style = div.style;
  const computed = window.getComputedStyle(element);
  const props = [
    'direction', 'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
    'fontSizeAdjust', 'lineHeight', 'fontFamily', 'textAlign', 'textTransform',
    'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing', 'tabSize',
  ];
  style.whiteSpace = 'pre-wrap';
  if (!isInput) style.wordWrap = 'break-word';
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.top = '0';
  style.left = '0';
  props.forEach((p) => { style[p] = computed[p]; });
  let text = element.value.substring(0, position);
  if (isInput) text = text.replace(/\s/g, '\u00a0');
  div.textContent = text;
  const span = document.createElement('span');
  span.textContent = element.value.substring(position) || '.';
  div.appendChild(span);
  document.body.appendChild(div);
  const rect = element.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();
  const divRect = div.getBoundingClientRect();
  const top = rect.top + (spanRect.top - divRect.top) - element.scrollTop;
  const left = rect.left + (spanRect.left - divRect.left) - element.scrollLeft;
  const height = spanRect.height || parseFloat(computed.lineHeight) || 20;
  document.body.removeChild(div);
  return { top, left, height };
}

const NO_SPELL_TYPES = new Set([
  'email', 'url', 'password', 'number', 'tel', 'search', 'hidden', 'file',
  'date', 'time', 'datetime-local',
]);

export default function SpellSuggest() {
  const [popover, setPopover] = useState(null);
  const [selIdx, setSelIdx] = useState(0);
  const dictRef = useRef([]);
  const popoverRef = useRef(null);
  const selIdxRef = useRef(0);

  // Keep refs in sync so global event listeners can read latest values
  // without re-registering.
  useEffect(() => { popoverRef.current = popover; }, [popover]);
  useEffect(() => { selIdxRef.current = selIdx; }, [selIdx]);

  // Load dictionary + subscribe to updates from the Custom Dictionary page.
  useEffect(() => {
    const loadDict = () => {
      const pw = sessionStorage.getItem('admin_password');
      if (!pw) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/dictionary`, {
        headers: { Authorization: `Basic ${btoa(':' + pw)}` },
      })
        .then((r) => (r.ok ? r.json() : { words: [] }))
        .then((data) => { dictRef.current = data.words || []; })
        .catch(() => {});
    };
    loadDict();
    const onUpd = (e) => { dictRef.current = e.detail || []; };
    window.addEventListener('pawsome:dictionary-updated', onUpd);
    return () => window.removeEventListener('pawsome:dictionary-updated', onUpd);
  }, []);

  useEffect(() => {
    const hide = () => setPopover(null);

    const isEligible = (el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.getAttribute('data-no-suggest') === 'true') return false;
      if (el.tagName === 'INPUT') {
        const t = (el.getAttribute('type') || 'text').toLowerCase();
        return !NO_SPELL_TYPES.has(t);
      }
      if (el.tagName === 'TEXTAREA') return true;
      return el.isContentEditable;
    };

    const handleInput = (e) => {
      const el = e.target;
      if (!isEligible(el)) return;

      let text;
      let caret;
      let node = null;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        text = el.value || '';
        caret = el.selectionStart || 0;
      } else {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return hide();
        const range = sel.getRangeAt(0);
        if (!range.collapsed) return hide();
        node = range.startContainer;
        if (!node || node.nodeType !== Node.TEXT_NODE) return hide();
        text = node.nodeValue || '';
        caret = range.startOffset;
      }

      const before = text.slice(0, caret);
      const after = text.slice(caret);
      const startMatch = before.match(/[A-Za-z][A-Za-z']*$/);
      if (!startMatch) return hide();
      // Only trigger after 3+ chars typed in the current word.
      if (startMatch[0].length < 3) return hide();
      const endMatch = after.match(/^[A-Za-z']*/);
      const wordStart = caret - startMatch[0].length;
      const wordEnd = caret + (endMatch ? endMatch[0].length : 0);
      const word = text.slice(wordStart, wordEnd);

      const suggestions = findSuggestions(word, dictRef.current);
      if (suggestions.length === 0) return hide();

      let pos;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        try { pos = getInputCaretRect(el, caret); }
        catch { return hide(); }
      } else {
        const range = window.getSelection().getRangeAt(0).cloneRange();
        range.collapse(true);
        let rect = range.getBoundingClientRect();
        if (!rect || (rect.top === 0 && rect.left === 0)) {
          rect = el.getBoundingClientRect();
        }
        pos = { top: rect.top, left: rect.left, height: rect.height || 20 };
      }

      setSelIdx(0);
      setPopover({
        top: pos.top + pos.height + 4,
        left: pos.left,
        word,
        wordStart,
        wordEnd,
        element: el,
        node,
        suggestions,
      });
    };

    const applySuggestion = (sugg) => {
      const s = popoverRef.current;
      if (!s || !sugg) return;
      const { element, wordStart, wordEnd, node } = s;
      if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
        const val = element.value;
        const newVal = val.slice(0, wordStart) + sugg + val.slice(wordEnd);
        const proto = element.tagName === 'INPUT'
          ? window.HTMLInputElement.prototype
          : window.HTMLTextAreaElement.prototype;
        const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
        setter.call(element, newVal);
        element.dispatchEvent(new Event('input', { bubbles: true }));
        const newCaret = wordStart + sugg.length;
        try { element.setSelectionRange(newCaret, newCaret); } catch {}
        element.focus();
      } else if (element.isContentEditable && node && node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue || '';
        node.nodeValue = text.slice(0, wordStart) + sugg + text.slice(wordEnd);
        const sel = window.getSelection();
        const range = document.createRange();
        const newOffset = Math.min(wordStart + sugg.length, node.nodeValue.length);
        range.setStart(node, newOffset);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        element.dispatchEvent(new InputEvent('input', { bubbles: true }));
      }
      setPopover(null);
    };

    // Expose to render-time click handler
    window.__pawsomeSpellApply = applySuggestion;

    const handleKey = (e) => {
      const s = popoverRef.current;
      if (!s) return;
      if (e.key === 'Escape') { e.preventDefault(); setPopover(null); return; }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        applySuggestion(s.suggestions[selIdxRef.current] || s.suggestions[0]);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelIdx((i) => (i + 1) % s.suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelIdx((i) => (i - 1 + s.suggestions.length) % s.suggestions.length);
        return;
      }
      // Any other typed printable character (space, punctuation) closes the popover
      // so we don't linger past the word being edited.
      if (e.key === ' ' || e.key === '.' || e.key === ',' || e.key === ';') {
        setPopover(null);
      }
    };

    const handleClick = (e) => {
      const s = popoverRef.current;
      if (!s) return;
      if (e.target.closest && e.target.closest('[data-spell-popover="1"]')) return;
      setPopover(null);
    };

    const handleBlur = (e) => {
      // Ignore blur that just moves focus to the popover itself
      setTimeout(() => {
        const active = document.activeElement;
        if (active && active.closest && active.closest('[data-spell-popover="1"]')) return;
        setPopover(null);
      }, 100);
    };

    const handleScroll = () => setPopover(null);

    document.addEventListener('input', handleInput, true);
    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('mousedown', handleClick, true);
    document.addEventListener('focusout', handleBlur, true);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('input', handleInput, true);
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('mousedown', handleClick, true);
      document.removeEventListener('focusout', handleBlur, true);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  if (!popover) return null;

  return (
    <div
      data-spell-popover="1"
      data-testid="spell-suggest-popover"
      style={{
        position: 'fixed',
        top: `${popover.top}px`,
        left: `${popover.left}px`,
        zIndex: 9999,
      }}
      className="min-w-[180px] rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-100">
        Did you mean
      </div>
      <ul className="py-1">
        {popover.suggestions.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              data-testid={`spell-suggest-option-${i}`}
              onClick={() => window.__pawsomeSpellApply && window.__pawsomeSpellApply(s)}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-teal-50 hover:text-teal-700 transition-colors ${
                i === selIdx ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'
              }`}
            >
              {s}
            </button>
          </li>
        ))}
      </ul>
      <div className="px-3 py-1 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-100">
        Tab/Enter to accept • Esc to dismiss
      </div>
    </div>
  );
}
